import {Avatar, Box} from '@mui/material';
import {GetStaticProps} from 'next';
import {useTranslation} from 'next-i18next';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {useEffect, useMemo} from 'react';
import {useQueryClient} from 'react-query';
import CampaignOverviewElement from '../../components/campaign/campaignOverview';
import {Page} from '../../components/common/container';
import {SectionTitle, Title} from '../../components/common/text';
import NetworkLink from '../../components/network/network';
import {useUser} from '../../provider/UserProvider';
import {useGetAuthorByUrl, useGetAuthorCampaigns} from '../../queries/campaignQueries';
import {AUTHOR_GET} from '../../queries/keys';
import {Author as AuthorDto, Role} from '../../types';
import {AuthorUrl} from '../../types/models/AuthorUrl';
import {URL_CLIENT, URL_SERVER_INTERNAL} from '../../utils/settings';

export default function Author(props: AuthorStaticProps) {
    const {dbUser} = useUser();
    const router = useRouter();
    const {i18n, t} = useTranslation('common');
    const {url, preview} = router.query;
    const queryClient = useQueryClient();

    if (props.data?.url) {
        queryClient.setQueryData<AuthorDto>(AUTHOR_GET + ':' + props.data?.url, props.data);
    }

    const {
        data: authorData,
        dataUpdatedAt,
        refetch: refetchAuthor,
    } = useGetAuthorByUrl(Boolean(url), url as string, props.data);

    const author = useMemo(() => {
        if (!props.data) {
            return authorData;
        }

        if (props.updatedAt > dataUpdatedAt) {
            return props.data;
        } else {
            return authorData;
        }
    }, [props.data, props.updatedAt, authorData, dataUpdatedAt]);

    const {data: campaigns, refetch: refetchCampaigns} = useGetAuthorCampaigns(
        Boolean(author?.author_id),
        author?.author_id
    );

    useEffect(() => {
        if (preview == '1' && refetchAuthor) {
            setTimeout(refetchAuthor, 1000);
        }
    }, [preview, refetchAuthor]);

    useEffect(() => {
        if (dbUser?.role != Role.CLIENT && refetchCampaigns) {
            refetchCampaigns();
        }
    }, [dbUser, refetchCampaigns]);

    if (!author) {
        return <></>;
    }

    const bio = author?.bios?.find((f) => f.lang == i18n.language);
    const hasNetwork = author?.networks?.length > 0;

    const hasCampaigns = campaigns?.length > 0;

    const image = author?.picture_url;

    // console.log('bio = ', bio?.bio);

    return (
        <Page>
            <AuthorSeo data={author} />
            <Title>{author?.display_name}</Title>
            <Avatar
                alt={author?.display_name}
                src={image}
                imgProps={{referrerPolicy: 'no-referrer'}}
                sx={{
                    width: 64,
                    height: 64,
                    marginRight: '5px',
                }}
            />
            <>
                <SectionTitle>{t('title.author_bio')}</SectionTitle>
                <Box>{bio?.bio}</Box>
            </>
            {hasNetwork && (
                <>
                    <SectionTitle>{t('title.author_networks')}</SectionTitle>
                    <Box>
                        {author?.networks?.map((n) => (
                            <NetworkLink network={n.network} url={n.url} key={n.url} />
                        ))}
                    </Box>
                </>
            )}
            {hasCampaigns && (
                <>
                    <SectionTitle>{t('title.author_campaign')}</SectionTitle>
                    <Box display="flex" flexDirection="row" justifyContent="flex-start">
                        {campaigns?.map((c) => (
                            <CampaignOverviewElement campaign={c} key={c.campaign_id} />
                        ))}
                    </Box>
                </>
            )}
        </Page>
    );
}

type AuthorSeoProps = {
    data: AuthorDto;
};

function AuthorSeo(props: AuthorSeoProps) {
    const {i18n, t} = useTranslation('common');
    // console.log('props = ', props);

    const title = `Kunekune | ${props.data?.display_name}`;
    const bio = props?.data?.bios?.find((f) => f.lang == i18n.language)?.bio;
    const subtitle = bio?.length > 155 ? bio?.substring(0, 155) : bio;
    const urlFr = URL_CLIENT + '/author/' + props.data?.url;
    const urlEn = URL_CLIENT + '/en/author/' + props.data?.url;

    const image = props.data?.picture_url;

    return (
        <Head>
            <title>{title}</title>
            <meta name="description" content={subtitle} />
            <meta property="og:url" content={urlFr} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={subtitle} />
            <meta property="og:image" content={image} />
            <meta property="og:image:alt" content={title} />
            <meta property="og:image:width" content="256" />
            <meta property="og:image:height" content="256" />
            <meta property="og:ttl" content="2419200" />
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:description" content={subtitle} />
            <meta property="twitter:image" content={image} />
            <meta property="twitter:image:alt" content={title} />
            <meta property="twitter:url" content={urlFr} />
            <meta property="twitter:site" content="@KuneKune" />
            <link hrefLang="fr" href={urlFr} rel="canonical" />
            <link hrefLang="en" href={urlEn} rel="alternate" />
        </Head>
    );
}

type AuthorStaticProps = {
    data: AuthorDto;
    updatedAt: number;
};

export const getStaticProps: GetStaticProps<AuthorStaticProps> = async (context) => {
    const translation = await serverSideTranslations(context.locale, ['common']);
    try {
        const author = context.params.url as string;
        let url = URL_SERVER_INTERNAL + '/author/url?url=' + author;
        let requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        };

        const result = await fetch(url, requestOptions);
        const json = await result.json();

        return {
            props: {
                data: json,
                updatedAt: Date.now(),
                ...translation,
            },
            revalidate: 60,
        };
    } catch (e) {
        console.log('getServerSideProps::error author url', e);
        return {
            props: {
                data: null,
                updatedAt: Date.now(),
                ...translation,
            },
            revalidate: 60,
        };
    }
};

export async function getStaticPaths({locales}) {
    try {
        let url = URL_SERVER_INTERNAL + '/author/list';
        let requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        };

        const result = await fetch(url, requestOptions);
        const urls = await result.json();

        const paths = urls
            .map((author: AuthorUrl) =>
                locales.map((locale) => ({
                    params: {url: author.url},
                    locale,
                }))
            )
            .flat();

        // console.log('paths = ', paths);

        return {
            paths: paths,
            fallback: true,
        };
    } catch (e) {
        console.log('getStaticPaths::error ', e);

        return {
            paths: [],
            fallback: true,
        };
    }
}
